import type { EpochRecord } from "../data/types";
import { epochDescriptor, stateColor } from "../data/types";

interface Props {
  epochs: EpochRecord[];
  gains: number[];
  soloIndex: number | null;
  onGainChange: (index: number, value: number) => void;
  onSoloToggle: (index: number) => void;
  onClearSolo: () => void;
  onReset: () => void;
}

/**
 * Per-epoch mixer for the all-epochs concurrent mode. Each row exposes a
 * level fader and a Solo button so the user can re-balance the parallel
 * sonification or focus on one epoch at a time. The entire control surface
 * is purely a listening aid; it does not represent a physical decomposition
 * of the source.
 */
export function EpochMixer(props: Props) {
  const { epochs, gains, soloIndex, onGainChange, onSoloToggle, onClearSolo, onReset } = props;

  return (
    <div className="epoch-mixer">
      <div className="epoch-mixer-header">
        <h2>Epoch mix</h2>
        <div className="epoch-mixer-actions">
          <button onClick={onClearSolo} disabled={soloIndex === null} title="Clear solo">
            Clear solo
          </button>
          <button onClick={onReset} title="Reset all faders to unity and clear solo">
            Reset
          </button>
        </div>
      </div>
      <div className="epoch-mixer-grid">
        {epochs.map((epoch, i) => {
          const gain = gains[i] ?? 1;
          const isSolo = soloIndex === i;
          const dimmed = soloIndex !== null && !isSolo;
          return (
            <div
              key={epoch.index}
              className={`epoch-mixer-row${dimmed ? " dim" : ""}${isSolo ? " solo" : ""}`}
            >
              <span className="epoch-mixer-tag" style={{ background: stateColor(epoch.state) }} />
              <span className="epoch-mixer-label" title={`${epoch.telescope} · ${epoch.instrument}`}>
                {epoch.date}
              </span>
              <input
                type="range"
                min={0}
                max={1.5}
                step={0.01}
                value={gain}
                onChange={(e) => onGainChange(i, parseFloat(e.target.value))}
                aria-label={`Volume for ${epoch.date} (${epochDescriptor(epoch) || "epoch"})`}
              />
              <span className="epoch-mixer-readout">{Math.round(gain * 100)}</span>
              <button
                className={`epoch-mixer-solo${isSolo ? " active" : ""}`}
                aria-pressed={isSolo}
                onClick={() => onSoloToggle(i)}
                title={isSolo ? "Soloed (click to clear)" : "Solo this epoch"}
              >
                S
              </button>
            </div>
          );
        })}
      </div>
      <p className="epoch-mixer-note">
        Listening aid only — the spectra are independent observations, not a physical
        decomposition of the source.
      </p>
    </div>
  );
}
